import { createSlice, createSelector, PayloadAction } from '@reduxjs/toolkit';
import { clearAssets } from '../../../redux/asset/actions';
import { getValue } from '../../../redux/asset/selectors/shared';
import { selectExcludedList } from '../../useFilters/filterSlice';
import { NamespacedStoreState } from '../types/store.types';
import { GridRowId } from '@innovyze/stylovyze';
import { GetGridDataAction, PaginatedAssets } from '../utils/types';

// Re-export shared actions and selectors so existing imports keep working
export * from '../../../redux/asset/actions';
export * from '../../../redux/asset/selectors/asset';

// ─── State ─────────────────────────────────────────────────────────────────────

export const initialState: PaginatedAssets = {
	loading: false,
	items: [],
	pagination: {
		current: 1,
		pages: 1,
		total: 10,
		limit: 10,
	},
	sort: undefined,
	sortDescending: false,
	filterModel: undefined,
};

// ─── Slice (actions + reducer) ─────────────────────────────────────────────────

const assetGridSlice = createSlice({
	name: 'assetGrid',
	initialState,
	reducers: {
		getGridData: (state, _action: PayloadAction<GetGridDataAction>) => {
			state.loading = true;
		},
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		getGridDataResolved: (_, action: PayloadAction<any>) =>
			({ ...action.payload, loading: false }) as PaginatedAssets,
		getGridDataRejected: state => {
			state.loading = false;
		},
		getGridDataReset: () => ({ ...initialState }),
	},
	extraReducers: builder => {
		builder.addCase(clearAssets.toString(), () => ({ ...initialState }));
	},
});

export const {
	getGridData,
	getGridDataResolved,
	getGridDataRejected,
	getGridDataReset,
} = assetGridSlice.actions;

export default assetGridSlice.reducer;

// ─── Selectors ─────────────────────────────────────────────────────────────────

export const selectAssetGridBase = createSelector(
	[getValue],
	moduleState => moduleState?.assetGrid,
);

export const selectPaginatedAssets = selectAssetGridBase;

export const selectGridLoading = createSelector(
	[selectPaginatedAssets],
	paginatedAssets => paginatedAssets?.loading,
);

export const selectGridItems = createSelector(
	[selectPaginatedAssets],
	paginatedAssets => paginatedAssets?.items ?? [],
);

export const selectGridRows = createSelector(
	[selectGridItems, selectExcludedList],
	(items, excludedList) =>
		items.map(({ segments, ...properties }) => ({
			...properties,
			...segments?.[0],
			excluded: excludedList.includes(properties._id),
		})),
);

const passInSelectionAndGridView = (
	_: NamespacedStoreState,
	selectedItems: GridRowId[],
	{ dataType, layerName }: { dataType?: string; layerName?: string },
) => ({ selectedItems, dataType, layerName });

export const selectSelectedAssets = createSelector(
	[selectGridRows, passInSelectionAndGridView],
	(rows, { selectedItems, dataType, layerName }) =>
		rows
			.filter(({ _id }) => selectedItems.includes(_id))
			.map(({ _id, assetId, assetType, layerSystemType, geometry }) => ({
				_id,
				assetId,
				assetType: assetType ?? layerName,
				systemType: layerSystemType ?? dataType,
				geometry,
			})),
);

export const selectGridPagination = createSelector(
	[selectPaginatedAssets],
	paginatedAssets => paginatedAssets?.pagination,
);

export const selectGridSort = createSelector(
	[selectPaginatedAssets],
	paginatedAssets => ({
		sort: paginatedAssets?.sort,
		sortDescending: paginatedAssets?.sortDescending,
	}),
);

